import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { X, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationsDropdown = ({ isOpen, onClose, onMarkAsRead }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      console.log('Loading notifications...');
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        withCredentials: true
      });
      console.log('Notifications loaded:', response.data);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWatchlist = async (notification) => {
    try {
      console.log('Adding show to watchlist:', notification.show_title);
      
      // Search for the show in TMDB
      const searchResponse = await axios.get(`${API}/search-tmdb`, {
        params: { q: notification.show_title },
        withCredentials: true
      });

      console.log('TMDB search result:', searchResponse.data);
      const tmdbData = searchResponse.data?.results?.[0] || {};

      const showData = {
        title: notification.show_title,
        image_url: tmdbData.poster_path ? `https://image.tmdb.org/t/p/w500${tmdbData.poster_path}` : notification.show_image_url || null,
        backdrop_url: tmdbData.backdrop_path ? `https://image.tmdb.org/t/p/w780${tmdbData.backdrop_path}` : null,
        release_date: tmdbData.release_date || null,
        genres: tmdbData.genres || null,
        status: 'Want to Watch',
        streaming_service: null,
        custom_service: null,
        personal_rating: null,
        personal_review: null,
        tmdb_id: tmdbData.tmdb_id || null,
        media_type: tmdbData.media_type || null
      };

      console.log('Posting show data:', showData);

      // Add to watchlist
      await axios.post(
        `${API}/shows`,
        showData,
        { withCredentials: true }
      );

      toast.success(`"${notification.show_title}" added to your watchlist!`);

      // Mark as read
      await markAsRead(notification.id);
      
      // Close the dropdown
      onClose();
    } catch (error) {
      console.error('Error adding show:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle duplicate show error
      if (error.response?.status === 409) {
        const errorData = error.response.data.detail;
        toast(
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
              {errorData.message || `You've already added "${notification.show_title}"`}
            </div>
            <div style={{ fontSize: '0.9rem' }}>
              Check My Shows to view or update this entry.
            </div>
          </div>,
          { 
            duration: 5000,
            icon: 'ℹ️'
          }
        );
        // Still mark as read since user acknowledged the recommendation
        await markAsRead(notification.id);
        
        // Close the dropdown even on duplicate
        onClose();
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Error adding show to watchlist');
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { withCredentials: true }
      );
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
      // Notify parent to refresh unread count
      if (onMarkAsRead) {
        onMarkAsRead();
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read when clicked (unless already read)
    if (!notification.read) {
      await markAsRead(notification.id);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        withCredentials: true
      });
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Error deleting notification');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="notifications-overlay" onClick={onClose} />
      <div className="notifications-dropdown">
        <div className="notifications-header">
          <h3>Notifications</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="notifications-body">
          {loading ? (
            <div className="loading-state">Loading...</div>
          ) : !notifications || notifications.length === 0 ? (
            <div className="empty-state">
              <p>No notifications yet</p>
              <p className="empty-subtitle">We'll notify you when friends follow you, recommend shows, or comment on your reviews!</p>
            </div>
          ) : (
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: 'pointer' }}
                >
                  <button
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification.id);
                    }}
                    title="Delete notification"
                  >
                    <X size={14} />
                  </button>

                  <div className="notification-content">
                    <div className="notification-header-section">
                      {notification.from_user_id === 'system' ? (
                        <>
                          <div className="system-icon">
                            <img src="/sheep-logo.png" alt="BaadTV" className="system-logo" />
                          </div>
                          <div className="notification-meta">
                            <span className="sender-name">BaadTV</span>
                            <span className="notification-time">{formatTime(notification.created_at)}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <Avatar className="notification-avatar">
                            <AvatarImage
                              src={notification.from_user?.picture}
                              alt={notification.from_user?.name}
                            />
                            <AvatarFallback>
                              {notification.from_user?.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="notification-meta">
                            <span className="sender-name">{notification.from_user?.name || 'Unknown'}</span>
                            <span className="notification-time">{formatTime(notification.created_at)}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {notification.type === 'show_recommendation' && (
                      <div className="recommendation-content">
                        {notification.show_image_url && (
                          <img
                            src={notification.show_image_url}
                            alt={notification.show_title}
                            className="show-poster"
                          />
                        )}
                        <div className="recommendation-text">
                          <p className="recommendation-message">
                            <strong>Recommended:</strong> {notification.show_title}
                          </p>
                          {notification.message && (
                            <p className="personal-message">"{notification.message}"</p>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleAddToWatchlist(notification)}
                            className="add-to-watchlist-btn"
                          >
                            <Plus size={16} />
                            Add to Want to Watch
                          </Button>
                        </div>
                      </div>
                    )}

                    {notification.type === 'comment' && (
                      <div className="comment-notification">
                        <p className="comment-message">
                          <strong>Commented on your review:</strong> {notification.show_title}
                        </p>
                        {notification.message && (
                          <p className="comment-preview">"{notification.message}"</p>
                        )}
                      </div>
                    )}

                    {notification.type === 'follow' && (
                      <div className="follow-notification">
                        <p className="follow-message">
                          <strong>Started following you!</strong> 🐑
                        </p>
                        <p className="follow-subtitle">They'll now see your reviews in their feed</p>
                      </div>
                    )}

                    {notification.type === 'new_episode' && (
                      <div className="episode-notification">
                        {notification.show_image_url && (
                          <img
                            src={notification.show_image_url}
                            alt={notification.show_title}
                            className="show-poster-small"
                          />
                        )}
                        <div className="episode-text">
                          <p className="episode-message">
                            <strong>{notification.show_title}</strong>
                          </p>
                          <p className="episode-detail">{notification.message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <style jsx>{`
          .notifications-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: transparent;
            z-index: 998;
          }

          .notifications-dropdown {
            position: fixed;
            top: 60px;
            right: 1rem;
            width: 400px;
            max-width: calc(100vw - 2rem);
            max-height: 600px;
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
            z-index: 999;
            display: flex;
            flex-direction: column;
          }

          .notifications-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid #333;
          }

          .notifications-header h3 {
            font-size: 1.1rem;
            font-weight: 600;
            color: #ffffff;
            margin: 0;
          }

          .close-btn {
            background: none;
            border: none;
            color: #b0b0b0;
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .close-btn:hover {
            color: #ffffff;
          }

          .notifications-body {
            flex: 1;
            overflow-y: auto;
          }

          .loading-state,
          .empty-state {
            text-align: center;
            padding: 3rem 1.5rem;
            color: #b0b0b0;
          }

          .empty-state p {
            margin: 0.5rem 0;
          }

          .empty-subtitle {
            font-size: 0.9rem;
            color: #666;
          }

          .notifications-list {
            padding: 0.5rem 0;
          }

          .notification-item {
            position: relative;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid #2a2a2a;
            transition: background 0.2s;
          }

          .notification-item:hover {
            background: #222;
          }

          .notification-item.unread {
            background: rgba(230, 47, 136, 0.05);
            border-left: 3px solid #E62F88;
          }

          .delete-btn {
            position: absolute;
            top: 0.75rem;
            right: 0.75rem;
            background: none;
            border: none;
            color: #666;
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            opacity: 0;
            transition: opacity 0.2s;
          }

          .notification-item:hover .delete-btn {
            opacity: 1;
          }

          .delete-btn:hover {
            color: #ff4444;
          }

          .notification-content {
            margin-right: 1.5rem;
          }

          .notification-header-section {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 0.75rem;
          }

          .notification-avatar {
            width: 36px;
            height: 36px;
          }

          .notification-meta {
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
          }

          .sender-name {
            font-weight: 600;
            color: #ffffff;
            font-size: 0.9rem;
          }

          .notification-time {
            font-size: 0.8rem;
            color: #b0b0b0;
          }

          .recommendation-content {
            display: flex;
            gap: 1rem;
            margin-top: 0.5rem;
          }

          .show-poster {
            width: 60px;
            height: 90px;
            object-fit: cover;
            border-radius: 4px;
            flex-shrink: 0;
          }

          .recommendation-text {
            flex: 1;
          }

          .recommendation-message {
            margin: 0 0 0.5rem 0;
            color: #ffffff;
            font-size: 0.9rem;
          }

          .recommendation-message strong {
            color: #E62F88;
          }

          .personal-message {
            margin: 0 0 0.75rem 0;
            color: #b0b0b0;
            font-size: 0.85rem;
            font-style: italic;
            padding: 0.5rem;
            background: #222;
            border-radius: 4px;
          }

          .comment-notification {
            padding: 0.5rem 0;
          }

          .comment-message {
            margin: 0 0 0.5rem 0;
            color: #ffffff;
            font-size: 0.9rem;
          }

          .comment-message strong {
            color: #E62F88;
          }

          .comment-preview {
            margin: 0;
            color: #b0b0b0;
            font-size: 0.85rem;
            font-style: italic;
            padding: 0.5rem;
            background: #222;
            border-radius: 4px;
            border-left: 3px solid #E62F88;
          }

          .add-to-watchlist-btn {
            background: #E62F88;
            color: #000000;
            font-size: 0.85rem;
            padding: 0.4rem 0.75rem;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
          }

          .add-to-watchlist-btn:hover {
            background: #E48BB6;
          }

          .follow-notification {
            margin-top: 0.5rem;
          }

          .follow-message {
            margin: 0 0 0.25rem 0;
            color: #ffffff;
            font-size: 0.9rem;
          }

          .follow-message strong {
            color: #E62F88;
          }

          .follow-subtitle {
            margin: 0;
            color: #b0b0b0;
            font-size: 0.85rem;
          }

          .system-icon {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            overflow: hidden;
            flex-shrink: 0;
          }

          .system-logo {
            width: 36px;
            height: 36px;
            object-fit: contain;
          }

          .episode-notification {
            display: flex;
            gap: 0.75rem;
            margin-top: 0.5rem;
            align-items: flex-start;
          }

          .show-poster-small {
            width: 45px;
            height: 67px;
            object-fit: cover;
            border-radius: 4px;
            flex-shrink: 0;
          }

          .episode-text {
            flex: 1;
          }

          .episode-message {
            margin: 0 0 0.25rem 0;
            color: #ffffff;
            font-size: 0.9rem;
          }

          .episode-message strong {
            color: #E62F88;
          }

          .episode-detail {
            margin: 0;
            color: #22c55e;
            font-size: 0.85rem;
            font-weight: 500;
          }

          @media (max-width: 768px) {
            .notifications-dropdown {
              width: calc(100vw - 2rem);
              right: 1rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default NotificationsDropdown;
